// Shared form validators.
//
// Add new validators here as we build them out. Each validator should:
//   - Accept the user-entered string
//   - Return true if valid (including "valid because empty/optional") or false if not
//   - Be pure — no side effects, no state

/**
 * Validates a job posting URL. Lenient about the scheme: a value without
 * http(s):// (e.g. "google.com/jobs") is accepted — callers normalize it with
 * ensureHttpProtocol() on save. Empty string is valid (the field is optional).
 *
 * Usage in forms:
 *   <TextField
 *     error={!isValidJobUrl(url)}
 *     helperText={!isValidJobUrl(url) ? 'Please enter a valid URL' : ''}
 *   />
 */
export function isValidJobUrl(s: string): boolean {
  if (!s) return true
  try {
    // Validate the normalized form so a scheme-less URL ("google.com/jobs")
    // is accepted; we store the normalized value on save.
    const u = new URL(ensureHttpProtocol(s))
    return u.protocol === 'http:' || u.protocol === 'https:'
  } catch {
    return false
  }
}

/**
 * Normalizes a user-entered URL for use in an anchor href. When the value has
 * no scheme (e.g. "www.linkedin.com/in/foo"), the browser would treat it as a
 * path relative to the current app origin and produce a broken link like
 * "https://dev.dreamjobhq.com/www.linkedin.com/in/foo". Prepending "https://"
 * makes it an absolute external link. Returns the input unchanged when it is
 * empty or already has an http(s) scheme.
 */
export function ensureHttpProtocol(url: string | null | undefined): string {
  if (!url) return ''
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}
